import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { TableCellsIcon } from "@heroicons/react/24/outline";

const EXCEL_TYPES = {
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

const isExcelFile = (file) =>
  Object.keys(EXCEL_TYPES).includes(file.type) || /\.(xlsx|xls)$/i.test(file.name);

/**
 * Pull FileSystemFileHandles off a drop when the browser exposes them.
 * A handle is what later lets "Fix Format" write back to the original file;
 * without one the app falls back to downloading a fixed copy.
 */
const getFilesFromEvent = async (event) => {
  const items = event.dataTransfer && event.dataTransfer.items;
  if (!items) return Array.from((event.target && event.target.files) || []);

  const files = [];
  for (const item of items) {
    if (item.kind !== "file") continue;

    if (typeof item.getAsFileSystemHandle === "function") {
      try {
        const handle = await item.getAsFileSystemHandle();
        if (handle && handle.kind === "file") {
          const file = await handle.getFile();
          file.fileHandle = handle;
          files.push(file);
          continue;
        }
      } catch (error) {
        // handle unavailable, fall through to the plain file
      }
    }

    const plain = item.getAsFile();
    if (plain) files.push(plain);
  }
  return files;
};

/**
 * @param {object} props
 * @param {(file: File, handle: FileSystemFileHandle|null) => void} props.onFileUpload
 * @param {boolean} props.isProcessing
 * @param {string} [props.openFileName] - the workbook currently open, "" when none
 * @param {boolean} [props.canSaveBack] - whether the app holds a handle to write into it
 */
const FileUpload = ({ onFileUpload, isProcessing, openFileName = "", canSaveBack = false }) => {
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (isExcelFile(file)) {
        onFileUpload(file, file.fileHandle || null);
      } else {
        alert("Please upload a valid Excel file (.xlsx or .xls)");
      }
    },
    [onFileUpload]
  );

  const canPickWithHandle =
    typeof window !== "undefined" && typeof window.showOpenFilePicker === "function";

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    getFilesFromEvent,
    accept: EXCEL_TYPES,
    multiple: false,
    disabled: isProcessing,
    // Clicking the zone used to open a plain file input, which hands back a File with no
    // FileSystemFileHandle. Saving then had no way to write to the original and silently
    // left a second copy in Downloads. Where the picker exists, use it for the click too,
    // so a handle is captured however the user opens the workbook.
    noClick: canPickWithHandle,
  });

  // Opening through the picker is the reliable way to get a writable handle
  const openWithPicker = async (event) => {
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();
    try {
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: [
          {
            description: "Excel workbook",
            accept: {
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                          "application/vnd.ms-excel": [".xls"],
            },
          },
        ],
      });
      if (!handle) return;
      const file = await handle.getFile();
      if (isExcelFile(file)) {
        onFileUpload(file, handle);
      } else {
        alert("Please upload a valid Excel file (.xlsx or .xls)");
      }
    } catch (error) {
      // the picker throws when the user cancels; nothing to report
    }
  };

  return (
    <div className="w-full">
      <div
        {...getRootProps({
          onClick: canPickWithHandle && !isProcessing ? openWithPicker : undefined,
        })}
        className={`dropzone ${isDragActive ? "active" : ""} ${
          isProcessing ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <input {...getInputProps()} />
        <div className="text-center">
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <div className="spinner"></div>
              <span className="text-gray-600">Processing file...</span>
            </div>
          ) : (
            <>
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-lg text-gray-600 mb-2">
                {isDragActive
                  ? "Drop the Excel file here..."
                  : "Drag & drop an Excel file here, or click to select"}
              </p>
              <p className="text-sm text-gray-500">Supports .xlsx and .xls files</p>

              {canPickWithHandle && (
                <button
                  type="button"
                  onClick={openWithPicker}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 shadow-sm"
                >
                  Choose file
                </button>
              )}
              {canPickWithHandle && (
                <p className="mt-2 text-xs text-gray-400">
                  Opening this way lets the app save back into the same file
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Outside the drop zone, so clicking the name does not open the file picker */}
      {openFileName && (
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <TableCellsIcon className="h-5 w-5 text-gray-400 shrink-0" aria-hidden="true" />
          <span className="text-gray-500">Open file:</span>
          <span className="font-medium text-gray-800 break-all">{openFileName}</span>
          {/* Without a handle, Fix Format and Save cannot write into it - say so up front */}
          {canPickWithHandle && !canSaveBack && (
            <span className="text-xs text-amber-700">
              (cannot save into this file; open it with Choose file to save)
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
